/**
 * PATH: packages/email-hub/src/feature/lib/composeFromEmail.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-COMPOSEFROMEMAIL-001
 * PURPOSE: Build the calendar "Schedule meeting from email" payload from
 *          an email row — the single, shared producer-side mapping that
 *          BOTH the right-click `EmailContextMenu` and the `EmailReader`
 *          toolbar button feed into `triggerComposeFromEmail`.
 * WHY:     The reader had NO visible "create a calendar invite" affordance
 *          (operator 2026-06-22: "I am trying to create a calendar invite
 *          here … there is no option here?"). The flow already existed, but
 *          only in the context menu. Surfacing it in the reader meant a
 *          second producer of the same payload — and a copy-pasted harvest
 *          loop is exactly the kind of latent drift the operator standing
 *          orders forbid. This is the one builder both call.
 * FLOW:
 *   ┌──────────────┐   ┌──────────────────────────┐   ┌────────────────────┐
 *   │ Email row     │ → │ buildComposeFromEmail-    │ → │ ComposeFromEmail-  │
 *   │ (subject,     │   │ Payload (harvest+dedupe   │   │ Payload (→ calendar│
 *   │  from/to/cc,  │   │  attendees, excerpt body) │   │  EventComposer)    │
 *   │  body, id)    │   └──────────────────────────┘   └────────────────────┘
 * DEPENDENCIES:
 *   - @quotech/ui (getRecipientEmail, EmailRecipient): canonical recipient
 *     normaliser so bare-string + object mailbox shapes both harvest.
 *   - @quotech/ui/calendar (ComposeFromEmailPayload): the receiver-owned
 *     contract shape.
 * RELATED:
 *   - EMAILHUB-FEATURE-COMPONENTS-EMAILCONTEXTMENU-001 (producer)
 *   - EMAILHUB-FEATURE-COMPONENTS-EMAILREADER-001 (producer)
 *   - packages/ui/src/calendar/composeFromEmail.ts (contract + trigger)
 */
import { getRecipientEmail, type EmailRecipient } from '@quotech/ui'
import type { ComposeFromEmailPayload } from '@quotech/ui/calendar'

/** Cap the description excerpt so the composer textarea stays readable —
 *  operators can paste more from the original message pane if needed. */
const BODY_EXCERPT_MAX = 1000

/** Minimal structural shape we read off an email to build the payload.
 *  Both the list-cache row and the hydrated detail satisfy it. */
export interface ComposeFromEmailSource {
  /** Master Django Email PK (UUID) — stamped onto Event.source_email_id. */
  id: string
  subject?: string | null
  from_address?: string | null
  from_name?: string | null
  to_addresses?: EmailRecipient[] | null
  cc_addresses?: EmailRecipient[] | null
  body_text?: string | null
  body_preview?: string | null
}

/**
 * Map an email to the calendar compose payload. Pure I→O — no side effects,
 * no navigation (the caller passes the result to `triggerComposeFromEmail`).
 *
 * Attendees are harvested sender-first, then To, then Cc, deduped by
 * lower-cased email. The current operator is intentionally NOT seeded
 * (calendar_app adds the organiser automatically); the sender is, because
 * a meeting derived from a thread is almost always "schedule a call with
 * this person" — one click removes the chip if not.
 */
export function buildComposeFromEmailPayload(
  email: ComposeFromEmailSource,
): ComposeFromEmailPayload {
  const description = (email.body_text || email.body_preview || '').slice(
    0,
    BODY_EXCERPT_MAX,
  )

  const seen = new Set<string>()
  const harvest = (list: EmailRecipient[] | null | undefined) => {
    if (!list) return []
    return list
      .map((r) => ({
        email: getRecipientEmail(r),
        name: typeof r === 'string' ? undefined : r.name,
      }))
      .filter((r) => {
        if (!r.email) return false
        const key = r.email.toLowerCase().trim()
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      .map((r) => ({ email: r.email, display_name: r.name || undefined }))
  }

  // Seed the sender first so the dedupe set suppresses a To/Cc repeat.
  if (email.from_address) {
    seen.add(email.from_address.toLowerCase().trim())
  }
  const attendees = [
    ...(email.from_address
      ? [{ email: email.from_address, display_name: email.from_name || undefined }]
      : []),
    ...harvest(email.to_addresses),
    ...harvest(email.cc_addresses),
  ]

  return {
    source_email: email.id,
    title: email.subject || '(no subject)',
    description,
    attendees,
  }
}
